import { api } from "./api.js";
import { db, settle, failed, cacheGet, cacheSet,
         pendingMatches, settleMatch } from "./outbox.js";

/**
 * The background sync.
 *
 * This lives OUTSIDE any screen on purpose. The first version drained inside
 * the scorer component, so scoring four balls offline and then reloading — or
 * simply tapping Home — left them queued forever. The server showed 0/0 while
 * the phone showed 11/0, which is the worst possible outcome for a scoring app.
 *
 * The lease token is persisted for the same reason: it lived in component
 * state, so a reload lost it and every queued delivery would have been refused
 * with NO_LEASE even if something had tried to send them.
 */

const LEASE_KEY = (matchId: string) => `lease:${matchId}`;

export async function rememberLease(matchId: string, token: string | null): Promise<void> {
  await cacheSet(LEASE_KEY(matchId), token);
}

export async function recallLease(matchId: string): Promise<string | null> {
  const hit = await cacheGet<string | null>(LEASE_KEY(matchId));
  return hit?.value ?? null;
}

export interface SyncReport {
  sent: number;
  /** Refused by the engine and kept for inspection, never discarded. */
  refused: number;
  remaining: number;
  /** Tried enough times that they will not be retried automatically. */
  stuck: number;
}

/**
 * How many times to re-offer a delivery the engine refused.
 *
 * Not infinite, because one impossible ball would block every ball behind it.
 * Not one, because a refusal is often about ordering — a delivery can be
 * rejected while the ball before it is still queued, and succeed once that
 * lands.
 */
const MAX_TRIES = 3;

let running = false;

/**
 * Send everything waiting, oldest first.
 *
 * Serial, because a ledger is a sequence. Safe to call at any time: the server
 * dedupes on clientEventId, so a delivery that did land during a flaky moment
 * is not scored twice.
 */
export async function syncNow(): Promise<SyncReport> {
  if (running || !navigator.onLine) {
    const rows = await db.pending.toArray();
    return { sent: 0, refused: 0, remaining: rows.length,
             stuck: rows.filter((r) => r.tries >= MAX_TRIES).length };
  }
  running = true;
  let sent = 0, refused = 0, created = 0;
  try {
    const all = await db.pending.orderBy("createdAt").toArray();
    const byMatch = new Map<string, typeof all>();
    for (const row of all) {
      const list = byMatch.get(row.matchId) ?? [];
      list.push(row);
      byMatch.set(row.matchId, list);
    }

    /**

     * Matches created offline go first.

     *

     * A delivery queued against a match the server has never heard of is

     * refused, and after three refusals it is marked stuck — so sending balls

     * before the match that holds them would quietly strand a whole innings.

     * In order, and one at a time, because the request is idempotent on

     * matchId but only if it is not racing itself.

     */

    for (const nm of await pendingMatches()) {

      try {

        const r = await api<{ ok?: boolean; matchId?: string }>(

          `/api/v1/clubs/${nm.clubId}/quick-match`, { json: nm.body });

        // A duplicate answers 200 with the existing fixture, which is a success:

        // it means an earlier attempt got through before the reply came back.

        if (r.ok) { await settleMatch(nm.matchId); created++; }

        else if (r.status === 401) break;

      } catch { break; }

    }


    for (const [matchId, rows] of byMatch) {
      let lease = await recallLease(matchId);
      let triedFresh = false;

      /**
       * Take the pen back.
       *
       * The drain only ever used the token it had remembered, and a lease
       * expires. So the sequence that stranded a real match was: score some
       * balls offline, lease quietly expires server-side, phone comes back on
       * wifi, every queued ball is refused LEASE_LOST, and the loop breaks
       * keeping them "safe" — forever. The app looked fine and the deliveries
       * were never going anywhere.
       *
       * Breaking is right when somebody else is genuinely scoring. It is wrong
       * when nobody holds the pen at all, which is the common case hours after
       * a match: these balls are this scorer's and there is no one to conflict
       * with. So try to acquire, once, and only give up if that is refused.
       */
      const claimLease = async (): Promise<boolean> => {
        if (triedFresh) return false;
        triedFresh = true;
        try {
          const r = await api<{ ok?: boolean; leaseToken?: string }>(
            `/api/v1/matches/${matchId}/lease`, { json: {} });
          if (!r.ok || !r.data.leaseToken) return false;
          lease = r.data.leaseToken;
          await rememberLease(matchId, lease);
          return true;
        } catch { return false; }
      };

      // No remembered token at all — a reinstall, or a lease that was never
      // written. Without this the whole queue is refused before it starts.
      if (!lease) await claimLease();

      for (const row of rows) {
        // Already refused repeatedly: skip it rather than spend a request, but
        // leave it in place so it is still visible and exportable.
        if (row.tries >= MAX_TRIES) continue;
        try {
          const r = await api<{ success: boolean; error?: { code: string } }>(
            `/api/v1/matches/${matchId}/commands`,
            { json: { clientEventId: row.id, command: row.command, leaseToken: lease } });
          if (r.ok) { await settle(row.id); sent++; continue; }

          /**
           * Signed out, not refused.
           *
           * With a 24-hour session a scorer can be signed out between overs.
           * That says nothing about whether the delivery is valid, so it must
           * not spend one of its three attempts — three unlucky balls would
           * otherwise be marked stuck by an expiry that had nothing to do with
           * them. Stop, keep the order, try again once signed back in.
           */
          if (r.status === 401) break;

          const code = r.data.error?.code;
          if (code === "NO_LEASE" || code === "LEASE_LOST") {
            // Try to take the pen back, then have one more go at this ball.
            if (await claimLease()) {
              const retry = await api<{ success: boolean; error?: { code: string } }>(
                `/api/v1/matches/${matchId}/commands`,
                { json: { clientEventId: row.id, command: row.command, leaseToken: lease } });
              if (retry.ok) { await settle(row.id); sent++; continue; }
            }
            // Somebody else really is scoring. Keep the deliveries — they are
            // not wrong, they just cannot be applied until the pen comes back.
            break;
          }
          /**
           * The engine refused it. Keep it.
           *
           * This used to delete the delivery outright — a ball the scorer
           * recorded, gone with no record of what it was and no way to tell
           * them. For a scoring app that is the worst possible outcome, and it
           * happened silently.
           *
           * Marking it failed keeps it in the outbox with its error and a try
           * count. It stops being retried after a few attempts so it cannot
           * jam the balls behind it, but it stays on the device where it can
           * be seen and exported.
           */
          await failed(row.id, code ?? "refused");
          refused++;
        } catch {
          break;   // offline again; stop and keep the order
        }
      }
    }
  } finally {
    running = false;
  }
  const rows = await db.pending.toArray();
  return {
    sent, refused,
    remaining: rows.length,
    stuck: rows.filter((r) => r.tries >= MAX_TRIES).length,
  };
}

export async function countPending(): Promise<number> {
  try { return await db.pending.count(); } catch { return 0; }
}


/**
 * Start syncing in the background, for the life of the app.
 *
 * Three triggers, because on mobile none of them is reliable alone: the online
 * event does not always fire when a connection recovers, and a backgrounded tab
 * may not run timers at all.
 */
export function startSync(onChange: (r: SyncReport) => void): () => void {
  const go = () => { void syncNow().then(onChange); };
  window.addEventListener("online", go);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") go();
  });
  const timer = setInterval(go, 15_000);
  go();
  return () => {
    window.removeEventListener("online", go);
    clearInterval(timer);
  };
}
